import React, { useState, useEffect, useCallback } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getTopicData } from "../../../api/topicDataAPI";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { useObjectData, useEnumData } from "../../../hooks/useLocalStorageData";
import { formatEpoch } from "../../../utils/dateUtils";
import { useSnackbar } from "../../../hooks/useSnackbar";
import {
  TextField,
  Button,
  Box,
  CardContent,
  Typography,
  Stack,
  Card,
  Snackbar,
  Alert,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { exportTelemetryHistory } from "../../../utils/telemetryHistoryExporter";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";

export default function HistoryDataGrid() {
  const { t } = useTranslation();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasInitialData, setHasInitialData] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const { interval, isRunning } = useRefreshInterval();
  const isPageVisibility = usePageVisibility();
  const apiRef = useGridApiRef();
  const intervalMs = Math.max((interval || 15) * 1000, 1000);
  // Use custom hooks to get data from localStorage
  const objectData = useObjectData();
  const enumData = useEnumData();

  const getNodeIdFromUrl = useCallback(() => {
    return getPlaceIdFromPathname(location.pathname);
  }, [location.pathname]);
  const getObjectNameById = useCallback(
    (objectId) => {
      const object = objectData.find(
        (item) => item.code === parseInt(objectId)
      );
      return object ? object.name : `Object ${objectId}`;
    },
    [objectData]
  );

  const fetchData = useCallback(
    async (
      startTimestamp = 0,
      endTimestamp = Math.floor(Date.now() / 1000)
    ) => {
      try {
        setLoading(true);
        const nodeId = getNodeIdFromUrl();
        if (!nodeId) {
          setLoading(false);
          return;
        }

        const data = await getTopicData(nodeId, startTimestamp, endTimestamp);

        // If data is null (error occurred), retry if we don't have initial data yet
        if (data === null) {
          if (!hasInitialData) {
            console.warn("Failed to fetch initial data, will retry on next interval");
            setErrorMessage("Waiting for valid data from server...");
            // Initialize with empty columns so the grid at least renders
            if (columns.length === 0) {
              setColumns([
                {
                  field: "date",
                  headerName: t("nodeComponents.telemetryHistory.date"),
                  width: 200,
                  minWidth: 180,
                  align: "center",
                  headerAlign: "center",
                },
              ]);
            }
          } else {
            console.warn("Failed to fetch data, keeping previous state");
            setErrorMessage("Error fetching data, showing previous data");
          }
          setLoading(false);
          return;
        }

        // Clear error message on successful data fetch
        setErrorMessage("");

        if (data && data.length > 0) {
          const excludeKeys = [
            "_id",
            "NID",
            "AID",
            "DSN",
            "topic",
            "DTYPE",
            "DID",
            "epoch",
          ];
          const allKeys = data.reduce((keys, item) => {
            Object.keys(item).forEach((key) => {
              if (!excludeKeys.includes(key) && !keys.includes(key)) {
                keys.push(key);
              }
            });
            return keys;
          }, []);

          const dynamicColumns = [
            {
              field: "date",
              headerName: t("nodeComponents.telemetryHistory.date"),
              width: 200,
              minWidth: 180,
              align: "center",
              headerAlign: "center",
            },
            ...allKeys.map((key) => {
              const objectItem = objectData.find(
                (objData) => objData.code === parseInt(key)
              );

              // Enum tipli kolonlar için valueOptions oluştur
              const isEnumType = objectItem && objectItem.type === 1;

              // Sadece bu kolonda bulunan unique değerleri bul
              const uniqueValuesInColumn = isEnumType
                ? [...new Set(data.map((item) => item[key]).filter((v) => v !== undefined && v !== null))]
                : [];

              // Sadece data'da bulunan enum değerlerini filtrele
              const enumOptions = isEnumType
                ? uniqueValuesInColumn
                    .map((val) => {
                      const enumItem = enumData.find((item) => item.enumVal === parseInt(val));
                      return enumItem ? { value: enumItem.name1, label: enumItem.name1 } : null;
                    })
                    .filter((item) => item !== null)
                : undefined;

              return {
                field: key,
                headerName: getObjectNameById(key),
                width: 150,
                minWidth: 120,
                align: "center",
                headerAlign: "center",
                // Enum tipli kolonlar için singleSelect kullan
                ...(isEnumType && enumOptions && enumOptions.length > 0
                  ? {
                      type: "singleSelect",
                      valueOptions: enumOptions,
                    }
                  : {}),
                valueGetter: (_value, row) => {
                  const cellValue = row[key];
                  if (cellValue === undefined || cellValue === null) {
                    return cellValue;
                  }

                  if (isEnumType) {
                    const enumValue = parseInt(cellValue);
                    const enumItem = enumData.find(
                      (item) => item.enumVal === enumValue
                    );
                    if (enumItem) {
                      return enumItem.name1;
                    }
                  }
                  return String(cellValue);
                },
                renderCell: (params) => {
                  if (params.value === undefined || params.value === null) {
                    return params.value;
                  }
                  return String(params.value);
                },
              };
            }),
          ];

          const gridRows = data.map((item, index) => {
            const row = {
              id: index,
              date: formatEpoch(item.epoch || 0),
            };
            allKeys.forEach((key) => {
              if (item[key] !== undefined && item[key] !== null) {
                const objectItem = objectData.find(
                  (objData) => objData.code === parseInt(key)

                );
                if (objectItem) {
                  row[key] = item[key];
                } else {
                  row[key] = item[key].toString();
                }
              }
            });

            return row;
          });

          // Batch state updates to prevent multiple re-renders
          setColumns(dynamicColumns);
          // Use setTimeout to ensure columns are set before rows
          setTimeout(() => {
            setRows(gridRows);
          }, 0);

          // Mark that we have successfully loaded initial data
          if (!hasInitialData) {
            setHasInitialData(true);
          }
        } else if (data && data.length === 0) {
          // Empty data array - clear the grid
          console.log("No data available for the selected period");
          setColumns([]);
          setRows([]);
        }
      } catch (error) {
        console.error("Error fetching telemetry data:", error);
        // Don't clear data on error, keep previous state
      } finally {
        setLoading(false);
      }
    },
    [getNodeIdFromUrl, t, objectData, enumData, getObjectNameById, hasInitialData, columns.length]
  );
  useEffect(() => {
    let intervalId;

    if (isRunning && isPageVisibility) {
      fetchData();

      intervalId = setInterval(() => {
        fetchData();
      }, intervalMs);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalMs, isRunning]);
  const handleFilterClick = () => {
    const startTimestamp = startDate ? new Date(startDate).getTime() / 1000 : 0;
    const endTimestamp = endDate
      ? new Date(endDate).getTime() / 1000
      : Math.floor(Date.now() / 1000);

    fetchData(startTimestamp, endTimestamp);
  };

  const handleManualRefresh = () => {
    const startTimestamp = startDate ? new Date(startDate).getTime() / 1000 : 0;
    const endTimestamp = endDate
      ? new Date(endDate).getTime() / 1000
      : Math.floor(Date.now() / 1000);

    fetchData(startTimestamp, endTimestamp);
  };

  const handleExportToExcel = () => {
    try {
      const nodeId = getNodeIdFromUrl();
      const result = exportTelemetryHistory(rows, columns, objectData, enumData, nodeId);

      if (result.success) {
        showSnackbar(`Telemetri geçmişi başarıyla indirildi: ${result.fileName}`, 'success');
      } else {
        showSnackbar(result.error || 'Rapor oluşturulurken bir hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Excel export hatası:', error);
      showSnackbar('Rapor indirilemedi', 'error');
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          {" "}
          <CardContent>
            {" "}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box sx={{ width: { xs: "0px", md: "200px" } }} />{" "}
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" } }}
              >
                {t("nodeComponents.telemetryHistory.title")}
              </Typography>
              <Box sx={{ flexGrow: 1, display: { xs: "none", md: "block" } }} />
              <Stack
                direction={{ xs: "column", sm: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                width={{ xs: "100%", md: "auto" }}
                mt={{ xs: 2, md: 0 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  width="100%"
                  justifyContent="space-between"
                >
                  <Box sx={{ width: { xs: "100%", sm: "48%" } }}>
                    <TextField
                      label={t("nodeComponents.telemetryHistory.startDate")}
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ width: { xs: "100%", sm: "48%" } }}>
                    <TextField
                      label={t("nodeComponents.telemetryHistory.endDate")}
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      fullWidth
                    />{" "}
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  width="100%"
                  justifyContent="space-between"
                >
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "48%" },
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="third"
                      onClick={handleFilterClick}
                      startIcon={<FilterAltIcon />}
                      sx={{
                        height: 40,
                        flexGrow: 1,
                        minWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        px: 2,
                      }}
                    >
                      {t("nodeComponents.telemetryHistory.filter")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleManualRefresh}
                      startIcon={<RefreshIcon />}
                      sx={{
                        height: 40,
                        display: { xs: "none", sm: "flex" },
                        flexGrow: 1,
                        minWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        px: 2,
                      }}
                    >
                      {t("nodeComponents.telemetryHistory.refresh")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleExportToExcel}
                      startIcon={<FileDownloadIcon />}
                      sx={{
                        height: 40,
                        display: { xs: "none", sm: "flex" },
                        flexGrow: 1,
                        minWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        px: 2,
                      }}
                    >
                      {t("nodeComponents.telemetryHistory.export", {
                        defaultValue: "Export",
                      })}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleManualRefresh}
                      sx={{
                        height: 40,
                        p: 0,
                        flexGrow: 1,
                        display: { xs: "flex", sm: "none" },
                        minWidth: 0,
                        minHeight: 0,
                        width: 40,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <RefreshIcon />
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleExportToExcel}
                      sx={{
                        height: 40,
                        p: 0,
                        flexGrow: 1,
                        display: { xs: "flex", sm: "none" },
                        minWidth: 0,
                        minHeight: 0,
                        width: 40,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      aria-label={t("nodeComponents.telemetryHistory.export", {
                        defaultValue: "Export",
                      })}
                    >
                      <FileDownloadIcon />
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Card sx={{ borderWidth: 2, borderStyle: "solid", borderColor: "warning.main" }}>
            <CardContent>
              <Typography color="warning.main" variant="body2">
                {errorMessage}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
      <Box>
        {" "}
        <DataGrid
          columns={columns}
          rows={rows}
          loading={loading}
          disableSelectionOnClick
          apiRef={apiRef}
          pagination
          initialState={{
            pagination: {
              paginationModel: { pageSize: 50 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          slots={{
            toolbar: () => (
              <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
              </GridToolbarContainer>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              minWidth: "100%",
            },
            "& .MuiDataGrid-main": {
              width: "100%",
              overflow: "auto",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "background.paper",
              position: "sticky",
              zIndex: 1,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              textAlign: "center",
            },
            "& .MuiDataGrid-cell": {
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-cellContent": {
              textAlign: "center",
            },
          }}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
